import CollectionManagerDialog from "@/components/CollectionManager";
import HBSelect from "@/components/HBSelect";
import { PRIVATETYPE } from "@/constants/board";
import { EVENT } from "@/constants/event";
import { VIEWTYPE2_ITEM_MAP } from "@/constants/viewType";
import useAppStore from "@/context/store";
import {
  getBoardsListByIds,
  getCollectionBoards,
  getCollections,
  getMuseBoards,
  getUserBoards,
  sortBoard,
  sortCollectionBoard,
  sortMuseBoard,
} from "@/services";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { openBindTelModal, openBoardModal } from "@/utils/open";
import sortBoardsByTitleAndCreateTime, {
  setBoardsTitlePinyin,
} from "@/utils/sortBoardsByTitleAndCreateTime";
import { useLocalStorageWithExpired } from "@/utils/useLocalStorage";
import useRequest from "@/utils/useReq";
import useUrlState from "@/utils/useUrlState";
import constantFactory from "@gaoding/constant-factory";
import {
  useEventListener,
  useMemoizedFn,
  useSetState,
  useUpdateEffect,
} from "ahooks";
import { arrayMoveImmutable } from "array-move";
import classNames from "classnames";
import { isEqual } from "lodash";
import {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import AllPinsBoard from "../../../AllPinsBoard";
import BoardList from "../../../BoardList";
import DraggableBoardList from "../../../DraggableBoardList";
import HBIcon from "../../../HBIcon";
import styles from "../../index.module.less";
import componentStyles from "./index.module.less";
// import CollectionList from "./CollectionList";
import Empty from "../Empty";
import Loading from "../Loading";
import TabPanel from "../TabPanel";
import UnorganizedPinList from "../UnorganizedPinList";

export const sortType = {
  Author: 0, // 作者排序
  TimeDown: 1, // 时间降序
  TimeUp: 2, // 时间升序
  RecentUpdates: 3, //最近更新
  Alphabet: 4, // A-Z排序
};

function BoardListPanel(
  {
    userStore,
    user,
    isMe,
    initBoards,
    initPins, // 给所有采集的画板卡片使用
    showUnorganized = true, // 是否显示未归类画板
    hideCreateBtn = false, // 是否隐藏创建画板按钮
    showUnorganizedPinList = false, // 是否显示未归类采集瀑布流
    isManageMode = false, // 是否是管理员模式
    showActions = true, // 是否显示排序按钮
    useLocalStorageSort = true, // 是否使用localStorage排序
  }: {
    userStore: any;
    user: IUser;
    isMe: boolean;
    initBoards: IBoardInfo[];
    initPins: any;
    showUnorganized?: boolean;
    hideCreateBtn?: boolean;
    showUnorganizedPinList?: boolean;
    isManageMode?: boolean;
    showActions?: boolean;
    useLocalStorageSort?: boolean;
  },
  ref: RefObject<{
    reload: () => void;
  }>
) {
  user = user ?? userStore.user;
  const { lastBoards, setLastBoards } = useAppStore(
    ({ lastBoards, setLastBoards }) => ({ lastBoards, setLastBoards })
  );

  const [query, setQuery] = useUrlState();
  const {
    collection_id: collectionId,
    filterType,
    sort: initSort,
    viewType = VIEWTYPE2_ITEM_MAP.default.value,
  } = query;

  const [sort, setSort] = useLocalStorageWithExpired(
    useLocalStorageSort ? "userBoardSort" : "",
    {
      defaultValue: Number(initSort) || sortType.Author,
    }
  );

  const [loading, setLoading] = useState(!initBoards);
  const [records, setRecords] = useState<IBoardInfo[]>(initBoards || []);
  const [onlyCreation] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);

  const [unorganizedBoard, setUnorganizedBoard] = useState(null);

  const [unorganizedPinListVisible, setUnorganizedPinListVisible] =
    useState(false);

  const prevParam = useRef<any>();

  // 加载画板列表数据
  const loadData = useMemoizedFn(async (currentRecords, refresh = false) => {
    // A-Z排序比较特殊，是前端取lastBoards也就该用户的所有画板数据根据拼音首字母进行排序
    if (sort === sortType.Alphabet && !collectionId) {
      setHasMore(false);
      return;
    }

    currentRecords = currentRecords || records;
    const PAGE_SIZE = 30;
    const api =
      filterType === "creation"
        ? getMuseBoards
        : collectionId
        ? getCollectionBoards
        : getUserBoards;

    const lastData = currentRecords[currentRecords.length - 1];
    const lastRecord = lastData?.seq || lastData?.board_id;
    let orderParam = {};

    switch (sort) {
      case sortType.TimeDown:
        orderParam = {
          order_by: "board_id",
          max: lastRecord,
        };
        break;
      case sortType.TimeUp:
        orderParam = {
          order_by: "board_id",
          since: lastRecord || 0,
        };
        break;
      case sortType.RecentUpdates:
        orderParam = {
          order_by: "updated_at",
          max: lastRecord,
        };
        break;
      default:
        orderParam = {
          max: lastRecord,
        };
        break;
    }
    const params = {
      collection_id: collectionId,
      username: user.urlname,
      urlname: user.urlname,
      limit: PAGE_SIZE,
      ...orderParam,
    };

    // 用于解决切换成按专辑筛选会重新加载闪一下的问题
    if (!refresh && isEqual({ ...params, api }, prevParam.current)) {
      return;
    }

    prevParam.current = { ...params, api };

    if (currentRecords && !currentRecords.length) {
      setRecords([]);
    }

    setLoading(true);
    const result = await api(params);
    setLoading(false);
    if (result.err) return;

    let boards = result.boards ?? result.user.boards;
    const len = boards.length;

    // 过滤掉 空的 未归类画板
    boards = boards.filter(
      (board) =>
        !(board.is_private === PRIVATETYPE.notClass && board.pins?.length === 0)
    );

    setHasMore(len >= PAGE_SIZE);

    if (currentRecords.length) {
      pvTracker(getPageData());
    }

    setRecords([...currentRecords, ...boards]);
  });

  // 暴露给父组件的刷新方法
  useImperativeHandle(ref, () => {
    return {
      reload: () => {
        loadData([], true);
      },
    };
  });

  // 监听画板列表数据更新
  useEventListener(EVENT.BOARDLIST_DATA_UPDATE, async (data) => {
    let board = data?.detail?.board;
    if (board?.board_id) {
      const newList = records.map((item) => {
        if (item.board_id === board?.board_id) {
          return board;
        } else {
          return item;
        }
      });
      setRecords(newList);
    }
  });

  // 专辑列表变化
  const onCollectionChange = useMemoizedFn((val) => {
    setQuery({
      collection_id: val,
      filterType: undefined,
      sort: sortType.Author,
    });

    setSort(sortType.Author);
  });

  // 加载专辑列表
  const loadCollections = useMemoizedFn(async () => {
    if (!user?.user_id) return;
    const { err, collections } = await getCollections(user.user_id);

    if (err) {
      return;
    }

    // 当刷新列表时发现已经没有此专辑时，则跳到全部页面
    if (
      collectionId &&
      !collections.find((item) => item.collection_id == collectionId)
    ) {
      onCollectionChange(null);
    }
    return collections;
  });

  // 获取专辑列表
  const { data: collections = [], refreshAsync: refreshCollection } =
    useRequest(
      async () => {
        return loadCollections();
      },
      { refreshDeps: [user] }
    );

  // 创建画板
  const onCreateClick = useCallback(() => {
    openBoardModal({
      name: "boardModal",
      collectionId: collectionId,
      onOk: async () => {
        await loadData([], true);
      },
    });
  }, [collectionId, loadData]);

  // 排序画板 结束
  const onEnd = useCallback(
    async ({ newIndex, oldIndex }) => {
      // 未归类画板返回永远是第一个， 所有但它不显示的话，则需要将索引加1, 因为未归类画板在列表中占位
      if (unorganizedBoard && !showUnorganized) {
        oldIndex = oldIndex + 1;
        newIndex = newIndex + 1;
      }

      const newRecords = arrayMoveImmutable(records, oldIndex, newIndex);
      const oldRecords = [...records];
      setRecords(newRecords);

      let result = null;
      if (collectionId) {
        // 专辑内画板排序
        result = await sortCollectionBoard({
          collection_id: collectionId,
          board_id: records[oldIndex]?.board_id,
          after: newRecords[newIndex - 1]?.board_id,
        });
      } else if (filterType === "creation") {
        // 原创画板排序
        result = await sortMuseBoard({
          board_id: records[oldIndex]?.board_id,
          after: newRecords[newIndex - 1]?.board_id,
        });
      } else {
        // 普通画板排序
        result = await sortBoard({
          board_id: records[oldIndex]?.board_id,
          after: newRecords[newIndex - 1]?.board_id,
        });
      }

      if (result?.err) {
        setRecords(oldRecords);
      }
    },
    [showUnorganized, records, collectionId, filterType]
  );

  // 带封面画板
  const [coverBoards, setCoverBoards] = useSetState({});

  const updateRef = useRef({
    timer: null,
    ids: new Set<number>(),
  });

  // 更新封面画板
  const updateBoardCover = useMemoizedFn((board) => {
    const id = board.board_id;
    if (sort !== sortType.Alphabet || coverBoards[id]) {
      return;
    }
    updateRef.current.ids.add(id);
    if (updateRef.current?.timer) {
      return;
    }

    updateRef.current.timer = setTimeout(async () => {
      const ids = [...updateRef.current?.ids];
      updateRef.current.ids.clear();
      const { boards, err } = await getBoardsListByIds(ids);
      if (err) {
        return;
      }

      const boardsMap = {};
      boards.forEach((board) => {
        boardsMap[board?.board_id] = board;
      });

      setCoverBoards(boardsMap);

      updateRef.current.timer = null;
    }, 0);
  });

  useMemo(async () => {
    let dataSource = records;

    // 显示未归类画板瀑布流的情况下，设置未归类画板
    if (showUnorganizedPinList && !unorganizedBoard) {
      const unorganizedBoard = dataSource.find(
        (item) => item.is_private === PRIVATETYPE.notClass
      );

      // 设置未归类画板
      setUnorganizedBoard(unorganizedBoard);
    }

    // 不显示未归类画板
    if (!showUnorganized) {
      dataSource = dataSource.filter(
        (item) => item.is_private !== PRIVATETYPE.notClass
      );
    }

    // 判断是否是A-Z排序
    const isAlphabetSort = sort === sortType.Alphabet && isMe;

    if (!isAlphabetSort) {
      return setDataSource(dataSource);
    }

    // 判断是A-Z排序再取取过封面的画板数据
    {
      let allBoards = lastBoards;
      if (allBoards.length <= 0) {
        return;
      }

      // 判断是否有拼音
      if (!allBoards?.[0]?._pinyin) {
        allBoards = await setBoardsTitlePinyin(lastBoards);
        setLastBoards(allBoards);
        return;
      }

      // 根据拼音排序
      let sortedBoards =
        sortBoardsByTitleAndCreateTime(
          filterType === "creation"
            ? allBoards.filter((board) => board?.extra?.is_creation)
            : allBoards
        ) || [];

      // 根据封面画板数据 和 采集画板数据 进行合并
      sortedBoards = sortedBoards.map((record) => {
        // 只取封面和采集
        return coverBoards[record.board_id]
          ? {
              ...record,
              cover: record?.cover
                ? record?.cover
                : coverBoards[record.board_id]?.cover,
              pins:
                record?.pins?.length > 0
                  ? record?.pins
                  : coverBoards[record.board_id]?.pins,
            }
          : record;
      });

      // 设置数据源
      return setDataSource(sortedBoards);
    }
  }, [
    records,
    showUnorganizedPinList,
    unorganizedBoard,
    showUnorganized,
    sort,
    isMe,
    lastBoards,
    filterType,
    setLastBoards,
    coverBoards,
  ]);

  const init = useCallback(async () => {
    setLoading(true);
    await loadData([]);
    setLoading(false);
  }, [loadData]);

  // 专辑编辑后，从新加载专辑和画板列表
  const onCollectionEdit = useMemoizedFn(() => {
    refreshCollection();
    loadData([], true);
  });

  const [displayCollectionManager] = useState(false);

  useUpdateEffect(() => {
    loadData([], true);
  }, [+collectionId, onlyCreation, sort, filterType]);

  useEffect(() => {
    if (!initBoards || !initBoards?.length) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // 创建画板卡片 组件
  const createBoardCard = useMemo(() => {
    if (hideCreateBtn) {
      return null;
    }

    return (
      <div
        className={classNames(
          styles.createRect,
          styles[viewType],
          styles.createRectBoard
        )}
        key={"createBtn"}
        onClick={onCreateClick}
        data-gd-click="button_click"
        data-button-name="新建画板"
      >
        <div className={styles.block}>
          <HBIcon type={"plus"} className={styles.icon} />
        </div>
        {viewType === VIEWTYPE2_ITEM_MAP.default.value && (
          <>
            <div className={styles.title}>创建画板</div>
            <div className={styles.desc}>创建你的个人画板</div>
          </>
        )}
      </div>
    );
  }, [hideCreateBtn, viewType, onCreateClick]);

  // “所有采集” 的画板卡片 组件
  const allPinsBoardCard = useMemo(() => {
    return (
      <AllPinsBoard
        allPinsCount={user.pin_count}
        urlname={user.urlname}
        initPins={initPins}
      />
    );
  }, [initPins, user.urlname, user.pin_count]);

  // 专辑下没有画板情况下，提示关联画板组件
  const noBoardTip = useMemo(() => {
    return (
      <div className="justify-center color-text-3">
        试试
        <a
          className="primary-text-btn"
          onClick={async () => {
            await openBindTelModal();
            CollectionManagerDialog.open({
              targetId: collectionId,
              async onCancel() {
                await refreshCollection();

                // 删除了当前的专辑则不刷新列表
                if (
                  collections.find(
                    (item) => item.collection_id === collectionId
                  )
                ) {
                  loadData([], true);
                }
              },
            });
          }}
        >
          关联画板
        </a>
        ，批量添加画板到专辑内
      </div>
    );
  }, [collections, collectionId, refreshCollection, loadData]);

  // 排序类型列表
  const { SORT_LIST, SORT_ITEM_MAP } = useMemo(() => {
    // 为了避免切换用户的情况
    return constantFactory("SORT", [
      {
        key: sortType.Author,
        label: "拖拽排序",
        value: sortType.Author,
      },
      {
        key: sortType.Alphabet,
        label: "A-Z排序",
        value: sortType.Alphabet,
      },
      {
        key: sortType.RecentUpdates,
        label: "最近更新",
        value: sortType.RecentUpdates,
      },
    ]);
  }, []);

  // 排序类型变化
  const onSortChange = useMemoizedFn((option) => {
    setQuery({ sort: option.value });
    setSort(option.value);
  });

  const actions = useMemo(() => {
    if (showActions) {
      return (
        <div className={componentStyles.actions}>
          <HBSelect
            allowClear={false}
            options={SORT_LIST}
            value={SORT_ITEM_MAP[sort] || SORT_ITEM_MAP[sortType.Author]}
            onChange={onSortChange}
          />
        </div>
      );
    }

    return null;
  }, [showActions, SORT_LIST, SORT_ITEM_MAP, sort, onSortChange]);

  const hasUnorganizedBoardPin = unorganizedBoard?.pin_count > 0;

  // 显示 未归类画板瀑布流
  useEffect(() => {
    const visible =
      !hasMore && showUnorganizedPinList && hasUnorganizedBoardPin;
    setUnorganizedPinListVisible(visible);
  }, [hasMore, showUnorganizedPinList, hasUnorganizedBoardPin]);

  return (
    <TabPanel actions={actions}>
      {/* 专辑列表 */}
      {/* <CollectionList
        list={collections}
        onCollectionEdit={onCollectionEdit}
        current={collectionId}
        onSelect={onCollectionChange}
        hasEdit={isMe}
      /> */}

      {loading && !records.length ? (
        <Loading />
      ) : (
        <div style={{ padding: "16px 0 60px", position: "relative" }}>
          {!records.length && !dataSource.length ? (
            isMe ? (
              <>
                {isManageMode ? createBoardCard : null}

                {collectionId ? (
                  noBoardTip
                ) : (
                  <div
                    data-gd-expose="resource_loc_expose"
                    data-resource-title="没有更多了"
                    data-resource-type="文字"
                    className="justify-center color-text-3"
                  >
                    <Empty
                      imageUrl={
                        "https://cdn.dancf.com/fe-assets/img/a52cf2370b7c7f53c751dfcd154fcbde.png"
                      }
                      title="还没有画板～"
                    />
                  </div>
                )}
              </>
            ) : (
              <Empty
                imageUrl={
                  "https://cdn.dancf.com/fe-assets/img/a52cf2370b7c7f53c751dfcd154fcbde.png"
                }
                title="还没有公开的画板～"
              />
            )
          ) : isMe && isManageMode ? (
            <DraggableBoardList
              key={viewType}
              dataSource={dataSource}
              onLoadMore={loadData}
              hideUser={true}
              hideTime={true}
              hasMore={hasMore}
              type={viewType}
              draggable={
                isMe && !displayCollectionManager && sort === sortType.Author
              }
              onDelete={(boardId) =>
                setRecords((records) =>
                  records.filter((record) => record.board_id !== boardId)
                )
              }
              endMessage={unorganizedPinListVisible ? null : undefined}
              // onOrderUpdate={setRecords}
              onEnd={onEnd}
              onBoardCardRender={updateBoardCover}
            >
              {createBoardCard}
            </DraggableBoardList>
          ) : dataSource.length ? (
            <BoardList
              key={viewType}
              dataSource={dataSource}
              onLoadMore={loadData}
              hideUser={true}
              hasMore={hasMore}
              type={viewType}
              endMessage={unorganizedPinListVisible ? null : undefined}
            >
              {allPinsBoardCard}
            </BoardList>
          ) : null}

          {/* 显示未归类画板的采集瀑布流 */}
          {unorganizedPinListVisible ? (
            <UnorganizedPinList
              board={unorganizedBoard}
              showEditBtn={isManageMode}
            />
          ) : null}
        </div>
      )}
    </TabPanel>
  );
}

export default forwardRef(BoardListPanel);
